import { useQuery } from "@apollo/client";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { Button, Checkbox, LoadingSpinner, Tag } from "pepsico-ds";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { KeywordGroupProductSelectionContext } from "../../../../../context/KeywordGroupProductSelectionContext";
import { listKeywordGroups } from "../../../../../graphql/queries/listKeywordGroups";
import ButtonGroupDynamic from "../../../../common/ButtonGroupDynamic";
import "../../../../common/table/table.scss";
import FiltersBar from "./FiltersBar";
import GridProducts from "./GridProducts";
import ListProducts from "./ListProducts";
import "./sku-product-select.scss";
import {
  areAllSelected,
  buildProductByIdMap,
  buildProductsDataList,
  buildSelectedProductsData,
  computeSelectAllIds,
  formatBrandOptions,
  mergeInitialAndAvailable,
  toggleSelection,
} from "./utils";

const SkuProductSelectComponent = ({ data, path, handleChange }) => {
  const {
    loading,
    keywordGroups,
    availableItems,
    setPreviousSelected,
    setFilters,
    initialFiltersData,
  } = useContext(KeywordGroupProductSelectionContext);

  const [viewType, setViewType] = useState("grid");
  const [selectedBrand, setSelectedBrand] = useState([]);
  const [keywordText, setKeywordText] = useState("");
  const [isAllSelected, setIsAllSelected] = useState(false);

  const timeoutRef = useRef(null);
  const gridContainerRef = useRef(null);
  const listContainerRef = useRef(null);
  const headerTableRef = useRef(null);
  const [gridSize, setGridSize] = useState({ width: 0, height: 0, gap: 0 });
  const [listSize, setListSize] = useState({ width: 0, height: 0 });
  const initialSelectedRef = useRef(data || []);
  const handleKeywordTextChange = (e) => {
    setKeywordText(e.target.value);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        keyword: e.target.value.trim(),
      }));
    }, 2000);
  };

  const { loading: loadingBrands, data: brandsData } = useQuery(listKeywordGroups, {
    variables: { type: "BRAND" },
  });

  const handleBrandSelection = (selected) => {
    setSelectedBrand(selected);
    setFilters((prev) => ({
      ...prev,
      keywordGroupIds: selected ? selected.map((item) => item.id) : [],
    }));
  };
  const formatOptions = (dataOptions) => formatBrandOptions(dataOptions);

  // Set previous selected only once on mount
  useEffect(() => {
    setPreviousSelected(initialSelectedRef.current || []);
  }, [setPreviousSelected]);

  // Keep combinedItems stable on selection changes and put initially selected first
  const combinedItems = useMemo(() => {
    const initialSelected = initialSelectedRef.current || [];
    return mergeInitialAndAvailable(initialSelected, availableItems || []);
  }, [availableItems]);

  // O(1) lookup maps/sets to avoid repeated array scans
  const productById = useMemo(
    () => buildProductByIdMap(keywordGroups),
    [keywordGroups]
  );

  const selectedIds = useMemo(() => new Set(data || []), [data]);

  // Measure containers to size the virtualizers without changing layout
  useEffect(() => {
    const roGrid = new ResizeObserver(() => {
      const el = gridContainerRef.current;
      if (!el) return;
      const styles = window.getComputedStyle(el);
      const gapPx = parseFloat(styles.columnGap || styles.gap || "0") || 0;
      setGridSize({ width: el.clientWidth, height: el.clientHeight, gap: gapPx });
    });
    const roList = new ResizeObserver(() => {
      const el = listContainerRef.current;
      if (!el) return;
      setListSize({ width: el.clientWidth, height: el.clientHeight });
    });
    if (gridContainerRef.current) roGrid.observe(gridContainerRef.current);
    if (listContainerRef.current) roList.observe(listContainerRef.current);
    return () => {
      roGrid.disconnect();
      roList.disconnect();
    };
  }, []);

  // Products data list display.
  const productsDataList = useMemo(
    () => buildProductsDataList(combinedItems, productById, selectedIds),
    [combinedItems, productById, selectedIds]
  );

  // Selected products data derived from current map. If some selected IDs are not in the current
  // keywordGroups (due to filters), they will be omitted here; we can enhance later with a cache.
  const selectedProductsData = useMemo(
    () => buildSelectedProductsData(data, productById),
    [data, productById]
  );

  useEffect(() => {
    setIsAllSelected(
      areAllSelected(productsDataList || [], selectedProductsData || [])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsDataList?.length, selectedProductsData?.length]);

  const headerData = [
    {
      header: "",
      columnKey: "checked",
      style: { width: "10%" },
      render: (row) => (
        <Checkbox
          checked={selectedIds.has(row.id)}
          onChange={() => onSelect(row.id)}
        />
      ),
    },
    {
      header: "Product",
      columnKey: "title",
      style: { width: "60%" },
    },
    {
      header: "SKU",
      columnKey: "sku",
      style: { width: "30%" },
    },
  ];

  const onSelect = useCallback(
    (productId) => {
      const updatedData = toggleSelection(data, productId);
      handleChange(path, updatedData);
    },
    [data, handleChange, path]
  );

  const onRemoveAll = () => {
    handleChange(path, []);
    setIsAllSelected(false);
  };

  const handleClearFilters = () => {
    if (selectedBrand.length !== 0) setSelectedBrand([]);
    if (keywordText) setKeywordText("");
    setFilters(initialFiltersData);
  };

  const handleSelectAll = () => {
    const allProductIds = computeSelectAllIds(productsDataList);
    if (isAllSelected) {
      handleChange(path, []);
    } else {
      handleChange(path, allProductIds);
    }
    setIsAllSelected(!isAllSelected);
  };

  return (
    <div className="sku-product-select-wrapper">
      <div className="header">
        <span className="font-heading-h6-bold">Available SKUs</span>
      </div>
      <div className="helpers">
        <ButtonGroupDynamic
          items={[
            {
              icon: "view_module",
              onClick: () => setViewType("grid"),
            },
            {
              icon: "view_list",
              onClick: () => setViewType("list"),
            },
          ]}
          size="medium"
          spacing="2px"
          variant="toggle"
          selectedIndex={viewType === "grid" ? 0 : 1}
        />
      </div>
      <FiltersBar
        keywordText={keywordText}
        onKeywordChange={handleKeywordTextChange}
        brandsData={formatOptions(brandsData)}
        selectedBrand={selectedBrand}
        onBrandChange={handleBrandSelection}
        loadingBrands={loadingBrands}
        onClearFilters={handleClearFilters}
        isAllSelected={isAllSelected}
        onSelectAll={handleSelectAll}
        disableSelectAll={productsDataList?.length === 0}
      />
      <div className="products-content">
        {loading && (
          <div className="loading-container">
            <LoadingSpinner size={{ height: "2rem", width: "2rem" }} />
          </div>
        )}
        {!loading &&
          (viewType === "grid" ? (
            <div className="products-grid" ref={gridContainerRef}>
              <GridProducts
                gridSize={gridSize}
                productsDataList={productsDataList}
                onSelect={onSelect}
              />
            </div>
          ) : (
            <div className="products-table" ref={listContainerRef}>
              {/* Table header (static) */}
              <div className="common-table-wrapper w-100">
                <table
                  className="common-table"
                  data-testid="table"
                  ref={headerTableRef}
                >
                  <thead>
                    <tr>
                      {headerData.map((header) => (
                        <th
                          key={header.columnKey}
                          className={header.className}
                          style={header.style}
                        >
                          {header.header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                </table>
              </div>
              <ListProducts
                listSize={listSize}
                gridSize={gridSize}
                productsDataList={productsDataList}
                headerData={headerData}
              />
            </div>
          ))}
        <div className="selected-products-container">
          <div className="selected-header">
            <span className="font-xsmall-bold">{data?.length || 0} selected</span>
            <Button
              size="small"
              variant="tertiary"
              text="Remove all"
              onClick={onRemoveAll}
            />
          </div>
          <div className="product-tags">
            {selectedProductsData.map((product) => (
              <Tag
                key={product.id}
                text={[product.sku, product.title].join(", ")}
                isRemovable={true}
                isCopyable={false}
                color="Auxiliary-600"
                onRemove={() => onSelect(product.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const SkuProductSelectControl = withJsonFormsControlProps(
  SkuProductSelectComponent
);

export { SkuProductSelectComponent };
